import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DiffEditor, useMonaco } from '@monaco-editor/react';
import { diagramHistoryApi, VersionContent, VersionSummary } from '../api/diagramHistoryApi';
import { useTheme } from '../hooks/useTheme';
// @ts-ignore — JS module, no type declarations
import { parsePlantUml } from '../utils/plantumlParser';
import { parseErdDsl, isErdDsl } from '../utils/erdParser';
// @ts-ignore — JS module, no type declarations
import { parseStructurizrDsl } from '../utils/structurizrParser';
// @ts-ignore — JS module, no type declarations
import { ElementFactory } from '../types/elements';
import ElementRenderer from './ElementRenderer';
import C4DiffViewer from './diagramHistory/C4DiffViewer';

interface Props {
  projectId: string;
  diagramId: string;
  diagramName: string;
  onClose: () => void;
}

type DiffMode = 'text' | 'visual';

interface DiffOverlay {
  added: Set<string>;
  removed: Set<string>;
  changed: Set<string>;
  elements: any[];
}

/** Uses _semanticId set by parsePlantUml; falls back to positional el-N. */
function semanticIds(elements: any[]): any[] {
  return elements.map((el, i) => ({
    ...el,
    id: (el as any)._semanticId ?? `el-${i}`,
  }));
}

/** Detect actual content type from content bytes (fallback for mismatched GitlabPath extension). */
function effectiveContentType(version: VersionContent): 'puml' | 'bpmn' | 'c4json' | 'erd' | 'structurizr' {
  const trimmed = version.content?.trimStart() ?? '';
  if (trimmed.startsWith('@startuml')) {
    return isErdDsl(trimmed) ? 'erd' : 'puml';
  }
  if (trimmed.startsWith('<')) return 'bpmn';
  if (trimmed.startsWith('workspace')) return 'structurizr';
  return version.content_type as 'c4json';
}

/** Parse a version's content into a flat array of canvas elements. */
function parseVersionElements(version: VersionContent): any[] {
  const ct = effectiveContentType(version);

  if (ct === 'puml') {
    try {
      const result = parsePlantUml(version.content, ElementFactory);
      const arr = Array.isArray(result) ? result : [];
      return semanticIds(arr);
    } catch (err) {
      console.error('[HistoryDiff] parsePlantUml threw:', err);
      return [];
    }
  }
  if (ct === 'erd') {
    try {
      const result = parseErdDsl(version.content, ElementFactory);
      return Array.isArray(result) ? result : [];
    } catch (err) {
      console.error('[HistoryDiff] parseErdDsl threw:', err);
      return [];
    }
  }
  if (ct === 'structurizr') {
    try {
      const result = parseStructurizrDsl(version.content, ElementFactory) as unknown as { elements: any[]; elementMap: Map<string, any> };
      return Array.isArray(result.elements) ? result.elements : [];
    } catch (err) {
      console.error('[HistoryDiff] parseStructurizrDsl threw:', err);
      return [];
    }
  }
  // c4json / default: raw JSON elements array
  try {
    const parsed = JSON.parse(version.content);
    const arr = Array.isArray(parsed) ? parsed : [];
    return arr;
  } catch (err) {
    console.error('[HistoryDiff] JSON.parse threw:', err, '| content preview:', version.content?.slice(0, 60));
    return [];
  }
}

/**
 * True only for PlantUML SEQUENCE diagrams: explicit participant/actor declarations,
 * OR message arrows with a colon label (A -> B : text).
 * Component/class diagrams use arrows WITHOUT colons, so they won't match.
 */
function isPumlSequence(content: string): boolean {
  if (/\bparticipant\s|\bactor\s/i.test(content)) return true;
  // Message arrow: <token> ->/--> <token> : <text>
  return /\S+\s*-+>\s*\S+\s*:.+/.test(content);
}


export default function DiagramHistoryModal({ projectId, diagramId, diagramName, onClose }: Props) {
  const { theme, currentTheme } = useTheme();
  const monacoInstance = useMonaco();
  const monacoTheme = currentTheme === 'dark' ? 'vs-dark' : 'vs';

  // Restore Monaco global theme when the modal unmounts.
  const monacoThemeRef = useRef(monacoTheme);
  monacoThemeRef.current = monacoTheme;
  useEffect(() => {
    return () => {
      monacoInstance?.editor.setTheme(monacoThemeRef.current);
    };
  }, [monacoInstance]);

  const [commits, setCommits] = useState<VersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromSHA, setFromSHA] = useState<string | null>(null);
  const [toSHA, setToSHA] = useState<string | null>(null);
  const [fromVersion, setFromVersion] = useState<VersionContent | null>(null);
  const [toVersion, setToVersion] = useState<VersionContent | null>(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [diffMode, setDiffMode] = useState<DiffMode>('text');

  const hasCacheSource = commits.some(c => c.source === 'db');

  useEffect(() => {
    setLoading(true);
    diagramHistoryApi
      .getHistory(projectId, diagramId)
      .then(setCommits)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [diagramId]);

  const handleCommitClick = useCallback((sha: string) => {
    if (!fromSHA) {
      setFromSHA(sha);
      return;
    }
    if (sha === fromSHA) {
      setFromSHA(null);
      setToSHA(null);
      return;
    }
    setToSHA(sha);
  }, [fromSHA]);

  useEffect(() => {
    if (!fromSHA || !toSHA) {
      setFromVersion(null);
      setToVersion(null);
      return;
    }
    setLoadingDiff(true);
    Promise.all([
      diagramHistoryApi.getVersion(projectId, diagramId, fromSHA),
      diagramHistoryApi.getVersion(projectId, diagramId, toSHA),
    ])
      .then(([from, to]) => {
        setFromVersion(from);
        setToVersion(to);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoadingDiff(false));
  }, [diagramId, fromSHA, toSHA]);

  const monacoLanguage = (ct: string | null | undefined): string => {
    if (ct === 'structurizr') return 'hcl';
    if (ct === 'c4json') return 'json';
    if (ct === 'bpmn') return 'xml';
    return 'plaintext';
  };

  // Visual diff is available for structurizr↔structurizr, erd↔erd, and sequence puml↔puml pairs.
  // Use effectiveContentType so diagrams stored with wrong extension still work.
  const fromType = fromVersion ? effectiveContentType(fromVersion) : null;
  const toType = toVersion ? effectiveContentType(toVersion) : null;
  const canVisual =
    fromVersion != null &&
    toVersion != null &&
    fromType === toType &&
    (
      fromType === 'structurizr' ||
      fromType === 'erd' ||
      (fromType === 'puml' &&
        isPumlSequence(fromVersion.content) &&
        isPumlSequence(toVersion.content))
    );

  const visualDisabledReason =
    fromVersion != null && toVersion != null && !canVisual
      ? fromType !== toType
        ? 'Версии имеют разные форматы'
        : fromType === 'puml'
          ? 'Визуальный diff поддерживает только диаграммы последовательности PlantUML'
          : 'Визуальный diff доступен для C4 (Structurizr DSL), ERD и PlantUML диаграмм'
      : undefined;


  // Theme-derived colours
  const bg = theme.toolbar?.background || (currentTheme === 'dark' ? '#1e1e2e' : '#ffffff');
  const border = theme.toolbar?.border || (currentTheme === 'dark' ? '#333' : '#e2e8f0');
  const text = theme.toolbar?.text || (currentTheme === 'dark' ? '#cdd6f4' : '#1e293b');
  const subtle = currentTheme === 'dark' ? '#6c7086' : '#94a3b8';
  const itemHover = currentTheme === 'dark' ? '#313244' : '#f1f5f9';
  const activeBtn = currentTheme === 'dark' ? '#89b4fa' : '#3b82f6';
  const activeBtnText = currentTheme === 'dark' ? '#1e1e2e' : '#ffffff';
  const inactiveBtn = currentTheme === 'dark' ? '#313244' : '#e2e8f0';
  const shaColor = currentTheme === 'dark' ? '#89b4fa' : '#2563eb';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
    }}>
      <div style={{
        background: bg, borderRadius: 10, width: '90vw', maxWidth: 1100,
        height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        border: `1px solid ${border}`,
      }}>
        {/* Header */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: text, fontWeight: 600, flex: 1 }}>
            История версий: {diagramName}
          </span>
          {hasCacheSource && (
            <span style={{ color: '#f59e0b', fontSize: 12 }}>&#9888; Данные из кэша</span>
          )}
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: text, cursor: 'pointer', fontSize: 18 }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Commit list */}
          <div style={{ width: 280, borderRight: `1px solid ${border}`, overflowY: 'auto', padding: 8 }}>
            {loading && <p style={{ color: text, padding: 8 }}>Загрузка...</p>}
            {error && <p style={{ color: '#ef4444', padding: 8 }}>{error}</p>}
            {!loading && !error && commits.length === 0 && (
              <p style={{ color: subtle, padding: 8 }}>История недоступна</p>
            )}
            {commits.map(c => {
              const isFrom = c.commit_sha === fromSHA;
              const isTo = c.commit_sha === toSHA;
              const highlight = isFrom || isTo;
              return (
                <div
                  key={c.commit_sha}
                  onClick={() => handleCommitClick(c.commit_sha)}
                  style={{
                    padding: '8px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 2,
                    background: highlight ? itemHover : 'transparent',
                    border: `1px solid ${highlight ? activeBtn : 'transparent'}`,
                  }}
                >
                  <div style={{ color: shaColor, fontSize: 11, fontFamily: 'monospace' }}>
                    {c.commit_sha.slice(0, 8)}
                    {isFrom && <span style={{ marginLeft: 4, color: '#22c55e', fontSize: 10 }}>FROM</span>}
                    {isTo && <span style={{ marginLeft: 4, color: '#f97316', fontSize: 10 }}>TO</span>}
                    {c.source === 'db' && <span style={{ marginLeft: 4, color: '#f59e0b', fontSize: 10 }}>кэш</span>}
                  </div>
                  <div style={{ color: text, fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.message || '—'}
                  </div>
                  <div style={{ color: subtle, fontSize: 11 }}>
                    {c.author} · {new Date(c.created_at).toLocaleDateString('ru-RU')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Diff panel */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Mode toggle */}
            <div style={{ padding: '8px 12px', borderBottom: `1px solid ${border}`, display: 'flex', gap: 8 }}>
              <button
                onClick={() => setDiffMode('text')}
                style={{
                  padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12, border: 'none',
                  background: diffMode === 'text' ? activeBtn : inactiveBtn,
                  color: diffMode === 'text' ? activeBtnText : text,
                }}
              >
                Текстовый
              </button>
              <button
                onClick={() => { if (canVisual) setDiffMode('visual'); }}
                disabled={!canVisual}
                title={visualDisabledReason}
                style={{
                  padding: '4px 12px', borderRadius: 4, fontSize: 12, border: 'none',
                  cursor: canVisual ? 'pointer' : 'not-allowed', opacity: canVisual ? 1 : 0.4,
                  background: diffMode === 'visual' ? activeBtn : inactiveBtn,
                  color: diffMode === 'visual' ? activeBtnText : text,
                }}
              >
                Визуальный
              </button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {/* Placeholder when no commits selected or loading */}
              {(!fromSHA || !toSHA || loadingDiff) && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: loadingDiff ? text : subtle }}>
                  {loadingDiff
                    ? 'Загрузка diff...'
                    : commits.length < 2
                      ? 'Нужно минимум 2 версии для сравнения'
                      : 'Выберите два коммита для сравнения'}
                </div>
              )}

              {/* DiffEditor stays mounted once versions are loaded to avoid Monaco dispose race condition.
                  Hidden via display:none when in visual mode. */}
              {fromSHA && toSHA && !loadingDiff && fromVersion && toVersion && (
                <div style={{ height: '100%', display: diffMode === 'text' ? 'block' : 'none' }}>
                  <DiffEditor
                    height="100%"
                    language={monacoLanguage(fromType)}
                    original={fromVersion.content}
                    modified={toVersion.content}
                    options={{ readOnly: true, renderSideBySide: true, fontSize: 13 }}
                    theme={monacoTheme}
                    keepCurrentOriginalModel={true}
                    keepCurrentModifiedModel={true}
                  />
                </div>
              )}

              {/* Visual diff canvas */}
              {fromSHA && toSHA && !loadingDiff && diffMode === 'visual' && canVisual && fromVersion && toVersion && (
                <div style={{ position: 'absolute', inset: 0 }}>
                  {fromType === 'erd' ? (
                    <ErdDiffViewer
                      fromContent={fromVersion.content}
                      toContent={toVersion.content}
                      isDark={currentTheme === 'dark'}
                    />
                  ) : fromType === 'puml' ? (
                    <PumlDiffViewer
                      fromContent={fromVersion.content}
                      toContent={toVersion.content}
                      isDark={currentTheme === 'dark'}
                    />
                  ) : (
                    <C4DiffViewer
                      fromContent={fromVersion.content}
                      toContent={toVersion.content}
                      isDark={currentTheme === 'dark'}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Diff overlay helpers ─────────────────────────────────────────────────────

function elBBox(el: any): { bx: number; by: number; bw: number; bh: number } | null {
  if (el.type === 'arrow') return null; // arrows use path colouring, not rect border
  if (el.radius != null) {
    return { bx: el.x ?? 0, by: el.y ?? 0, bw: el.radius * 2, bh: el.radius * 2 };
  }
  if (el.width != null && el.height != null) {
    return { bx: el.x ?? 0, by: el.y ?? 0, bw: el.width, bh: el.height };
  }
  // text fallback
  const fs: number = el.fontSize ?? 16;
  const content = String(el.content ?? '');
  const lines = content.split('\n');
  const bw = Math.max(...lines.map((l: string) => l.length), 1) * fs * 0.6;
  const bh = lines.length * fs;
  return { bx: (el.x ?? 0) - bw / 2, by: (el.y ?? 0) - bh / 2, bw, bh };
}

function arrowSvgPath(el: any): string {
  const pts: Array<{ x: number; y: number }> = el.pathPoints?.length
    ? el.pathPoints
    : [{ x: el.x ?? 0, y: el.y ?? 0 }, { x: el.x2 ?? el.x ?? 0, y: el.y2 ?? el.y ?? 0 }];
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
}

function DiffOverlayLayer({ elements, diffOverlay }: { elements: any[]; diffOverlay: DiffOverlay }) {
  const diffColor = (id: string): string | null => {
    if (diffOverlay.added.has(id))   return '#22c55e';
    if (diffOverlay.removed.has(id)) return '#ef4444';
    if (diffOverlay.changed.has(id)) return '#eab308';
    return null;
  };

  return (
    <g pointerEvents="none">
      {elements.map(el => {
        const c = diffColor(el.id);
        if (!c) return null;

        if (el.type === 'arrow') {
          return (
            <path key={`diff-${el.id}`}
              d={arrowSvgPath(el)}
              fill="none" stroke={c} strokeWidth={3} opacity={0.9}
            />
          );
        }

        const bbox = elBBox(el);
        if (!bbox) return null;
        const { bx, by, bw, bh } = bbox;
        const isRemoved = diffOverlay.removed.has(el.id);
        const isChanged = diffOverlay.changed.has(el.id);
        const isText    = el.type === 'text';

        return (
          <g key={`diff-${el.id}`}>
            <rect
              x={bx - 3} y={by - 3} width={bw + 6} height={bh + 6}
              fill={isRemoved ? 'rgba(239,68,68,0.12)' : 'none'}
              stroke={c} strokeWidth={2.5} rx={4} opacity={0.85}
            />
            {isText && isChanged && (
              <line
                x1={bx} y1={by + bh / 2}
                x2={bx + bw} y2={by + bh / 2}
                stroke={c} strokeWidth={1.5}
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

// ─── ErdDiffViewer ───────────────────────────────────────────────────────────

function ErdDiffViewer({ fromContent, toContent, isDark }: {
  fromContent: string;
  toContent: string;
  isDark: boolean;
}) {
  const canvasBg = isDark ? '#181825' : '#f8fafc';
  const noItemsColor = isDark ? '#6c7086' : '#94a3b8';

  const { elements, overlay } = useMemo((): { elements: any[]; overlay: DiffOverlay } => {
    const parse = (content: string): any[] => {
      try {
        const raw = parseErdDsl(content, ElementFactory);
        const arr = Array.isArray(raw) ? raw : [];
        // Remap id → _semanticId for stable diff matching across re-parses
        return arr.map((el: any) => el._semanticId ? { ...el, id: el._semanticId } : el);
      } catch {
        return [];
      }
    };

    const oldEls = parse(fromContent);
    const newEls = parse(toContent);
    const oldById = new Map<string, any>(oldEls.filter((e: any) => e?.id).map((e: any) => [e.id, e]));
    const newById = new Map<string, any>(newEls.filter((e: any) => e?.id).map((e: any) => [e.id, e]));

    const added   = new Set<string>();
    const removed = new Set<string>();
    const changed = new Set<string>();

    const fingerprint = (el: any): string => {
      const SKIP = new Set(['x', 'y', 'width', 'height', 'id', '_semanticId', 'pathPoints', 'x2', 'y2']);
      return JSON.stringify(Object.fromEntries(Object.entries(el).filter(([k]) => !SKIP.has(k))));
    };

    for (const [id, ne] of newById) {
      if (!oldById.has(id)) added.add(id);
      else if (fingerprint(oldById.get(id)) !== fingerprint(ne)) changed.add(id);
    }
    for (const id of oldById.keys()) {
      if (!newById.has(id)) removed.add(id);
    }

    const unionElements = [
      ...newEls.filter((e: any) => e?.id),
      ...oldEls.filter((e: any) => e?.id && !newById.has(e.id)),
    ];
    return { elements: unionElements, overlay: { added, removed, changed, elements: unionElements } };
  }, [fromContent, toContent]);

  if (elements.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: noItemsColor }}>
        Нет элементов для отображения
      </div>
    );
  }

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of elements) {
    if (el.type === 'arrow') {
      const pts: Array<{ x: number; y: number }> = el.pathPoints?.length
        ? el.pathPoints
        : [{ x: el.x ?? 0, y: el.y ?? 0 }, { x: el.x2 ?? el.x ?? 0, y: el.y2 ?? el.y ?? 0 }];
      for (const p of pts) {
        minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
      }
    } else {
      minX = Math.min(minX, el.x ?? 0); minY = Math.min(minY, el.y ?? 0);
      maxX = Math.max(maxX, (el.x ?? 0) + (el.width ?? 200));
      maxY = Math.max(maxY, (el.y ?? 0) + (el.height ?? 100));
    }
  }

  const pad = 40;
  const vb = `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;
  const { removed } = overlay;

  // SVG marker IDs use positional index to avoid Cyrillic/spaces in _semanticId breaking url(#...)
  const svgSafeId = (_id: string, i: number) => `erd-el-${i}`;

  return (
    <svg viewBox={vb} style={{ width: '100%', height: '100%', background: canvasBg }}>
      <g opacity={0.45}>
        {elements.map((el, i) => removed.has(el.id) ? (
          <ElementRenderer
            key={`r-${el.id}`}
            element={{ ...el, id: svgSafeId(el.id, i), selected: false } as any}
            zoom={1}
            showConnectionPoints={false}
          />
        ) : null)}
      </g>
      {elements.map((el, i) => !removed.has(el.id) ? (
        <ElementRenderer
          key={el.id}
          element={{ ...el, id: svgSafeId(el.id, i), selected: false } as any}
          zoom={1}
          showConnectionPoints={false}
        />
      ) : null)}
      <DiffOverlayLayer elements={elements} diffOverlay={overlay} />
    </svg>
  );
}

// ─── PumlDiffViewer ──────────────────────────────────────────────────────────

function PumlDiffViewer({ fromContent, toContent, isDark }: {
  fromContent: string;
  toContent: string;
  isDark: boolean;
}) {
  const canvasBg = isDark ? '#181825' : '#f8fafc';
  const noItemsColor = isDark ? '#6c7086' : '#94a3b8';

  const { elements, overlay } = useMemo((): { elements: any[]; overlay: DiffOverlay } => {
    const parse = (content: string): any[] => {
      try {
        const raw = parsePlantUml(content, ElementFactory);
        return semanticIds(Array.isArray(raw) ? raw : []);
      } catch {
        return [];
      }
    };

    const oldEls = parse(fromContent);
    const newEls = parse(toContent);

    const oldById = new Map<string, any>(oldEls.filter(e => e?.id).map(e => [e.id, e]));
    const newById = new Map<string, any>(newEls.filter(e => e?.id).map(e => [e.id, e]));

    const added   = new Set<string>();
    const removed = new Set<string>();
    const changed = new Set<string>();

    // Ignore geometry (x/y/width/height/points) — only semantic content matters for diff.
    // Participant boxes/lifelines change size when messages are added, but aren't "changed".
    const fingerprint = (el: any): string => {
      // Strip geometry and any id-like fields that differ between two independent parses.
      // attachedToElement references the raw generateUniqueId() id of the parent, not semantic.
      const SKIP = new Set(['x', 'y', 'width', 'height', 'id', '_semanticId',
        'pathPoints', 'x2', 'y2', 'attachedToElement', 'startConnection', 'endConnection', 'lifeline']);
      const rest = Object.fromEntries(Object.entries(el).filter(([k]) => !SKIP.has(k)));
      return JSON.stringify(rest);
    };

    for (const [id, ne] of newById) {
      if (!oldById.has(id)) added.add(id);
      else if (fingerprint(oldById.get(id)) !== fingerprint(ne)) changed.add(id);
    }
    for (const id of oldById.keys()) {
      if (!newById.has(id)) removed.add(id);
    }

    const unionElements = [
      ...newEls.filter(e => e?.id),
      ...oldEls.filter(e => e?.id && !newById.has(e.id)),
    ];

    return {
      elements: unionElements,
      overlay: { added, removed, changed, elements: unionElements },
    };
  }, [fromContent, toContent]);

  if (elements.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: noItemsColor }}>
        Нет элементов для отображения
      </div>
    );
  }

  // Compute SVG viewBox from element bounding boxes
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of elements) {
    if (el.type === 'arrow') {
      const pts: Array<{ x: number; y: number }> = el.pathPoints?.length
        ? el.pathPoints
        : [{ x: el.x ?? 0, y: el.y ?? 0 }, { x: el.x2 ?? el.x ?? 0, y: el.y2 ?? el.y ?? 0 }];
      for (const p of pts) {
        minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
      }
    } else if (el.radius != null) {
      minX = Math.min(minX, el.x ?? 0);
      minY = Math.min(minY, el.y ?? 0);
      maxX = Math.max(maxX, (el.x ?? 0) + el.radius * 2);
      maxY = Math.max(maxY, (el.y ?? 0) + el.radius * 2);
    } else if (el.width != null) {
      minX = Math.min(minX, el.x ?? 0);
      minY = Math.min(minY, el.y ?? 0);
      maxX = Math.max(maxX, (el.x ?? 0) + el.width);
      maxY = Math.max(maxY, (el.y ?? 0) + el.height);
    } else {
      minX = Math.min(minX, el.x ?? 0);
      minY = Math.min(minY, el.y ?? 0);
      maxX = Math.max(maxX, (el.x ?? 0) + 80);
      maxY = Math.max(maxY, (el.y ?? 0) + 20);
    }
  }

  const pad = 40;
  const vb = `${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`;

  const { removed } = overlay;

  // ElementRenderer builds SVG marker ids from element.id via `marker-${id}-arrowhead-end`.
  // Spaces and special chars in semantic ids (e.g. Cyrillic message text) break url(#...).
  // Give renderer positional ids; DiffOverlayLayer keeps original semantic ids for colour lookup.
  const svgSafeId = (id: string, i: number) => `puml-el-${i}`;

  return (
    <svg viewBox={vb} style={{ width: '100%', height: '100%', background: canvasBg }}>
      {/* Removed elements rendered at reduced opacity */}
      <g opacity={0.45}>
        {elements.map((el, i) => removed.has(el.id) ? (
          <ElementRenderer
            key={`r-${el.id}`}
            element={{ ...el, id: svgSafeId(el.id, i), selected: false } as any}
            zoom={1}
            showConnectionPoints={false}
          />
        ) : null)}
      </g>
      {/* All other elements at full opacity */}
      {elements.map((el, i) => !removed.has(el.id) ? (
        <ElementRenderer
          key={el.id}
          element={{ ...el, id: svgSafeId(el.id, i), selected: false } as any}
          zoom={1}
          showConnectionPoints={false}
        />
      ) : null)}
      {/* Diff colour overlay on top — uses original semantic ids for colour lookup */}
      <DiffOverlayLayer elements={elements} diffOverlay={overlay} />
    </svg>
  );
}
